import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Divider,
  Alert
} from '@mui/material';
import { useLanguage } from '../../i18n/LanguageContext';
import { Routine, FetchWithAuth } from '../../types';
import { API_URL } from '../../utils';
import { commonDialogStyles } from '../common/dialogStyles';

interface ImportRoutineDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (routineId: number, dayId: number) => Promise<void>;
  selectedDate: Date;
  fetchWithAuth: FetchWithAuth;
  onServiceUnavailable?: () => void;
}

export const ImportRoutineDialog: React.FC<ImportRoutineDialogProps> = ({
  open,
  onClose,
  onImport,
  selectedDate,
  fetchWithAuth,
  onServiceUnavailable
}) => {
  const { t } = useLanguage();
  const [routines, setRoutines] = useState<Routine[]>([]);
  const [selectedRoutineId, setSelectedRoutineId] = useState<number | ''>('');
  const [selectedDayId, setSelectedDayId] = useState<number | ''>('');
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedRoutine = routines.find(r => r.id === selectedRoutineId);
  const selectedDay = selectedRoutine?.days.find(d => d.id === selectedDayId);

  const loadActiveRoutines = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchWithAuth(`${API_URL}/routines`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch routines: ${response.status}`);
      }
      
      const data = await response.json();
      const activeRoutines = Array.isArray(data) ? data.filter(r => r.is_active) : [];
      setRoutines(activeRoutines);
      
      if (activeRoutines.length === 0) {
        setError(t('importRoutine.noActiveRoutines'));
      }
    } catch (err: any) {
      console.error('Error fetching routines:', err);
      setError(t('importRoutine.loadError'));
      
      if (err.isServiceUnavailable && onServiceUnavailable) {
        onServiceUnavailable();
      }
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, t, onServiceUnavailable]);

  // Load active routines when dialog opens
  useEffect(() => {
    if (open) {
      loadActiveRoutines();
      // Reset selections
      setSelectedRoutineId('');
      setSelectedDayId('');
      setError(null);
    }
  }, [open, loadActiveRoutines]);

  const handleClose = () => {
    setSelectedRoutineId('');
    setSelectedDayId('');
    setError(null);
    onClose();
  };

  const handleImport = async () => {
    if (!selectedRoutineId || !selectedDayId || !selectedDay) return;

    setImporting(true);
    try {
      await onImport(selectedRoutineId as number, selectedDayId as number);
      handleClose();
    } catch (error: any) {
      console.error('Import failed:', error);
      setError(t('importRoutine.importError'));
    } finally {
      setImporting(false);
    }
  };

  const handleRoutineChange = (routineId: number | '') => {
    setSelectedRoutineId(routineId);
    setSelectedDayId(''); // Reset day selection when routine changes
  };

  const canImport = selectedRoutineId && selectedDayId && selectedDay && selectedDay.exercises.length > 0 && !importing;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth sx={commonDialogStyles}>
      <DialogTitle>
        {t('importRoutine.title')} - {selectedDate.toLocaleDateString()}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>{t('common.loading')}</Typography>
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : routines.length === 0 ? (
            <Alert severity="info">{t('importRoutine.noActiveRoutines')}</Alert>
          ) : (
            <>
              {/* Routine Selection */}
              <FormControl fullWidth>
                <InputLabel>{t('importRoutine.selectRoutine')}</InputLabel>
                <Select
                  value={selectedRoutineId}
                  label={t('importRoutine.selectRoutine')}
                  onChange={(e) => handleRoutineChange(e.target.value as number | '')}
                >
                  {routines.map((routine) => (
                    <MenuItem key={routine.id} value={routine.id}>
                      <Box>
                        <Typography>{routine.name}</Typography>
                        {routine.description && (
                          <Typography variant="caption" color="text.secondary">
                            {routine.description}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Day Selection */}
              {selectedRoutine && (
                <FormControl fullWidth>
                  <InputLabel>{t('importRoutine.selectDay')}</InputLabel>
                  <Select
                    value={selectedDayId}
                    label={t('importRoutine.selectDay')}
                    onChange={(e) => setSelectedDayId(e.target.value as number | '')}
                  >
                    {selectedRoutine.days.map((day) => (
                      <MenuItem key={day.id} value={day.id}>
                        <Box>
                          <Typography>
                            {t('routines.dayNumber')} {day.day_number}
                            {day.day_name && `: ${day.day_name}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {day.exercises.length} {t('routines.exercises')}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Preview of exercises to import */}
              {selectedDay && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    {t('importRoutine.preview')} ({selectedDay.exercises.length} {t('routines.exercises')})
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {t('importRoutine.allExercisesNote')}
                  </Alert>
                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, maxHeight: 300, overflow: 'auto' }}>
                    <List dense>
                      {selectedDay.exercises.map((exercise, index) => (
                        <React.Fragment key={exercise.id || index}>
                          <ListItem>
                            <ListItemText
                              primary={exercise.exercise?.name || t('routines.selectExercise')}
                              secondary={
                                <Box>
                                  {exercise.sets.map((set, setIndex) => (
                                    <Typography key={setIndex} variant="body2" component="div">
                                      {set.sets} {t('routines.sets')} × {set.reps} {t('workout.reps')}
                                      {set.notes && ` - ${set.notes}`}
                                    </Typography>
                                  ))}
                                  {exercise.notes && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                      {t('routines.exerciseNotes')}: {exercise.notes}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                          {index < selectedDay.exercises.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={importing}>
          {t('common.cancel')}
        </Button>
        <Button 
          onClick={handleImport} 
          variant="contained" 
          disabled={!canImport}
          startIcon={importing ? <CircularProgress size={20} /> : null}
        >
          {importing ? t('importRoutine.importing') : t('importRoutine.import')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 